import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Radio, History, FileBarChart, Monitor, Bell, Settings } from 'lucide-react';
import { haptics } from '../../lib/haptics';
import { sounds } from '../../lib/sounds';

interface TabItem {
  to: string;
  label: string;
  icon: React.ElementType;
  badge?: number;
  pulseDot?: boolean;
}

const TABS: TabItem[] = [
  { to: '/admin/live', label: 'Live', icon: Radio, pulseDot: true },
  { to: '/admin/activity', label: 'Activity', icon: History },
  { to: '/admin/reports', label: 'Reports', icon: FileBarChart },
  { to: '/admin/devices', label: 'Devices', icon: Monitor },
  { to: '/admin/notifications', label: 'Alerts', icon: Bell },
];

export function IosBottomTabBar() {
  const location = useLocation();

  // Only render inside /admin/ routes
  if (!location.pathname.startsWith('/admin')) {
    return null;
  }

  const handleTabClick = (isActive: boolean) => {
    if (!isActive) {
      haptics.selection();
      sounds.playTapSound();
    }
  };

  return (
    <nav
      aria-label="Mobile Navigation"
      className="fixed bottom-3 inset-x-3 z-40 max-w-md mx-auto md:hidden pointer-events-auto select-none"
    >
      <div className="ios-dock rounded-3xl p-1.5 flex items-center justify-around shadow-2xl">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive =
            tab.to === '/admin'
              ? location.pathname === '/admin' || location.pathname === '/admin/'
              : location.pathname.startsWith(tab.to);

          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              onClick={() => handleTabClick(isActive)}
              className={`relative flex flex-col items-center justify-center py-2 px-3 rounded-2xl ios-press transition-all ${
                isActive
                  ? 'text-zinc-900 dark:text-white font-bold'
                  : 'text-zinc-500 dark:text-zinc-400 hover:text-black dark:hover:text-zinc-200'
              }`}
            >
              {/* Active sliding background indicator */}
              {isActive && (
                <div className="absolute inset-0 rounded-2xl bg-black/[0.06] dark:bg-white/[0.12] border border-black/5 dark:border-white/20 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.8)] dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.25)] animate-ios-enter pointer-events-none" />
              )}

              <div className="relative">
                <Icon
                  className={`w-5 h-5 transition-transform duration-200 ${
                    isActive ? 'scale-110 text-pulse-blue' : ''
                  }`}
                />
                {tab.pulseDot && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-[#0a0a0d] animate-pulse" />
                )}
              </div>

              <span className="text-[10px] font-medium mt-1 tracking-tight">
                {tab.label}
              </span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
